'use client'

import type { GraphQLResult } from 'aws-amplify/api'
import { fetchUserAttributes } from 'aws-amplify/auth'

import { cognitoMonitoring } from './cognitoMonitoring.client'

import { getAmplifyClient } from '@/services/amplifyClient'
import { updateOrganizationMemberMutation } from '@/services/graphql/team'
import { organizationMemberbyEmail } from '@/services/user/user.graphql'
import { devLog } from '@/utils/dev.utility'

const client = getAmplifyClient()

type OrganizationMemberByEmailQuery = {
  organizationMemberbyEmail: {
    items?: Array<{
      id: string
      userID?: string | null
      status?: string | null
    } | null> | null
  }
}

type PendingInviteRecord = {
  id: string
  userID?: string | null
}

type AuthenticatedUserIdentity = {
  currentUserId: string
  email: string
}

type GraphQLErrorLike = {
  message?: string | null
}

function hasGraphQLErrors(
  errors?: ReadonlyArray<GraphQLErrorLike> | null,
): errors is ReadonlyArray<GraphQLErrorLike> {
  return Array.isArray(errors) && errors.length > 0
}

async function getAuthenticatedUserIdentity(): Promise<AuthenticatedUserIdentity | null> {
  try {
    const attributes = await cognitoMonitoring.observe(
      'profile',
      'signed_in',
      () => fetchUserAttributes(),
    )
    const currentUserId = (attributes['custom:userID'] ?? attributes.sub) as
      | string
      | undefined
    const email =
      typeof attributes.email === 'string'
        ? attributes.email.toLowerCase().trim()
        : ''

    if (!currentUserId || !email) {
      return null
    }

    return { currentUserId, email }
  } catch (error) {
    devLog('Error resolving authenticated user identity for invite sync', error)
    return null
  }
}

export async function reconcilePendingOrganizationInvitesForAuthenticatedUser(): Promise<void> {
  try {
    const identity = await getAuthenticatedUserIdentity()
    if (!identity) return

    const { currentUserId, email } = identity
    const queryResult = (await client.graphql({
      query: organizationMemberbyEmail,
      variables: {
        email,
        status: { eq: 'PENDING' },
      },
    })) as GraphQLResult<OrganizationMemberByEmailQuery>

    if (hasGraphQLErrors(queryResult.errors)) {
      devLog(
        'GraphQL errors loading pending organization invites during post-auth sync',
        queryResult.errors,
      )
      return
    }

    const pendingInvites =
      queryResult.data?.organizationMemberbyEmail.items ?? []
    const invitesToUpdate = pendingInvites.filter(
      (invite): invite is PendingInviteRecord => {
        if (!invite?.id) return false
        return invite.userID !== currentUserId
      },
    )

    if (!invitesToUpdate.length) return

    const updates = await Promise.allSettled(
      invitesToUpdate.map((invite) =>
        client.graphql({
          query: updateOrganizationMemberMutation,
          variables: {
            input: {
              id: invite.id,
              userID: currentUserId,
            },
          },
        }),
      ),
    )

    for (const result of updates) {
      if (result.status === 'rejected') {
        devLog(
          'Error updating a pending organization invite during post-auth sync',
          result.reason,
        )
        continue
      }

      if (hasGraphQLErrors(result.value.errors)) {
        devLog(
          'GraphQL errors updating a pending organization invite during post-auth sync',
          result.value.errors,
        )
      }
    }
  } catch (error) {
    devLog('Error reconciling pending organization invites post-auth', error)
  }
}
